import React, { useCallback, useEffect, useMemo, useState } from "react";
import SideBar from "../Components/SideBar";
import CreateAdmin from "../Components/CreateAdmin";
import UnifiedTable from "../Components/UsersManagement/UnifiedTable";
import AdminListTable from "../Components/UsersManagement/AdminListTable";
import UserPermissionModal from "../Components/UsersManagement/UserPermissionModal";
import AdminRoleModal from "../Components/UsersManagement/AdminRoleModal";
import SuperCityAdminRoleModal from "../Components/UsersManagement/SuperCityAdminRoleModal";
import Select from "react-tailwindcss-select";
import { useTranslation } from "react-i18next";
import { role } from "../Constants/role";
import { fetchCreatedAdmins } from "../Services/SuperAdminApi";
import { getCities } from "../Services/citiesApi";
import { getCurrentUser } from "../Services/usersApi";
import {
  fetchCityAdminsLists,
  fetchUserslists,
  fetchSuperAdminsLists,
  updateUsersRole,
  updateUsersStatus,
} from "../Services/userManagement";

const UsersManagement = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("userList");
  const [adminList, setAdminList] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSuperCityAdminRoleModal, setShowSuperCityAdminRoleModal] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedSuperCityAdmin, setSelectedSuperCityAdmin] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [citiesList, setCitiesList] = useState([]);
  const [selectedCityFilter, setSelectedCityFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [userRole, setUserRole] = useState(role.User);

  const [usersList, setUsersList] = useState([]);
  const [cityAdminList, setCityAdminList] = useState([]);
  const [superCityAdminList, setSuperCityAdminList] = useState([]);

  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  //invite user list apis
  const fetchAdminList = useCallback(async () => {
    try {
      const res = await fetchCreatedAdmins();
      if (res.data) {
        setAdminList(res?.data?.data);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  //Fetch Cities
  const fetchCitiesData = useCallback(async () => {
    try {
      const citiesResponse = await getCities();
      const cities = citiesResponse.data.data?.map((city) => ({
        value: city.id,
        label: city.name,
      }));
      setCitiesList(cities);
    } catch (e) {
      console.error("Error fetching cities:", e);
    }
  }, []);

  //invite user api
  const handleAddAdmin = useCallback(
    (msg) => {
      if (msg === "success") {
        setSuccess(t("user_invited"));
      } else {
        setError(t("somethingWrong"));
      }
      setShowInviteModal(false);
      fetchAdminList();
    },
    [t, fetchAdminList]
  );

  const handleManagePermission = useCallback((user) => {
    setSelectedUser(user);
    setShowPermissionModal(true);
  }, []);

  //userLists
  const getUsersLists = useCallback(
    async (pageNo = 1, pageSize = 10) => {
      try {
        const { data } = await fetchUserslists(pageNo, pageSize, searchQuery);
        setUsersList(data);
      } catch (error) {
        console.log(error);
      }
    },
    [searchQuery]
  );

  //getCity admin Lists
  const getCityAdminLists = useCallback(
    async (pageNo = 1, pageSize = 10) => {
      try {
        const { data } = await fetchCityAdminsLists(
          pageNo,
          pageSize,
          selectedCityFilter?.value,
          searchQuery
        );
        setCityAdminList(data);
      } catch (error) {
        console.log(error);
      }
    },
    [selectedCityFilter, searchQuery]
  );

  //get super city admin lists
  const getSuperCityAdminLists = useCallback(
    async (pageNo = 1, pageSize = 10) => {
      if (!isSuperAdmin) return;
      try {
        const { data } = await fetchSuperAdminsLists(
          pageNo,
          pageSize,
          selectedCityFilter?.value,
          searchQuery
        );
        setSuperCityAdminList(data);
      } catch (error) {
        console.log(error);
      }
    },
    [isSuperAdmin, searchQuery, selectedCityFilter]
  );

  // user modal role api for update
  const handleSavePermission = useCallback(
    async (permissionData) => {
      if (!selectedUser?.id) return;

      try {
        await updateUsersRole(selectedUser.id, permissionData);
        setShowPermissionModal(false);
        setSelectedUser(null);
        getUsersLists();
        getCityAdminLists();
        getSuperCityAdminLists();
        fetchAdminList();
        setSuccess(t("role_changed_successfully"));
      } catch (error) {
        console.error("Error saving permissions:", error);
        setError(
          error?.response?.data?.message || "Failed to update permissions"
        );
      }
    },
    [
      selectedUser?.id,
      t,
      getUsersLists,
      getCityAdminLists,
      getSuperCityAdminLists,
    ]
  );

  const handleManageRole = useCallback((admin) => {
    setSelectedAdmin(admin);
    setShowRoleModal(true);
  }, []);

  const handleManageSuperCityAdminRole = useCallback((admin) => {
    setSelectedSuperCityAdmin(admin);
    setShowSuperCityAdminRoleModal(true);
  }, []);

  //change role city admin
  const handleSaveRole = useCallback(
    async (roleData) => {
      if (!selectedAdmin?.id) return;

      try {
        await updateUsersRole(selectedAdmin.id, roleData);
        setSuccess(t("role_changed_successfully"));
        getCityAdminLists();
        getUsersLists();
        getSuperCityAdminLists();
        setShowRoleModal(false);
        setSelectedAdmin(null);
        fetchAdminList();
      } catch (error) {
        console.error("Error saving role:", error);
        setError(
          error?.response?.data?.message || "Failed to update permissions"
        );
      }
    },
    [
      selectedAdmin?.id,
      t,
      getCityAdminLists,
      getUsersLists,
      getSuperCityAdminLists,
    ]
  );
  //update superadminlist role
  const handleSaveSuperCityAdminRole = useCallback(
    async (roleData) => {
      if (!selectedSuperCityAdmin?.id) return;

      try {
        await updateUsersRole(selectedSuperCityAdmin.id, roleData);

        setSuccess(t("role_changed_successfully"));
        getSuperCityAdminLists();
        getCityAdminLists();
        getUsersLists();
        setShowSuperCityAdminRoleModal(false);
        setSelectedSuperCityAdmin(null);
        fetchAdminList();
      } catch (error) {
        console.error("Error saving Super City Admin role:", error);
        setError(
          error?.response?.data?.message || "Failed to update permissions"
        );
      }
    },
    [
      selectedSuperCityAdmin?.id,
      t,
      getSuperCityAdminLists,
      getCityAdminLists,
      getUsersLists,
    ]
  );
  //handle status of user cityadmin and supercityadmin
  const handleAction = useCallback(
    async (id, type, pageNo, pageSize) => {
      try {
        // TODO: Implement action handlers (edit, delete, etc.)
        const { data } = await updateUsersStatus(id);
        if (type === "cityAdmin") {
          getCityAdminLists(pageNo, pageSize);
          setSuccess(t("city_admin_status"));
        } else if (type === "superCityAdmin") {
          getSuperCityAdminLists(pageNo, pageSize);
          setSuccess(t("super_city_admin_status"));
        } else if (type === "user") {
          getUsersLists(pageNo, pageSize);
          setSuccess(t("user_status"));
        }
      } catch (error) {
        console.error("Error updating user status:", error);
        setError(error?.response?.data?.message || "Failed to update status");
      }
    },
    [getCityAdminLists, getSuperCityAdminLists, getUsersLists, t]
  );

  useEffect(() => {
    getSuperCityAdminLists(1, 10);
  }, [getSuperCityAdminLists]);

  useEffect(() => {
    getCityAdminLists(1, 10);
  }, [getCityAdminLists]);

  useEffect(() => {
    getUsersLists(1, 10);
  }, [getUsersLists]);

  useEffect(() => {
    fetchAdminList();
  }, [fetchAdminList]);

  //get current user role
  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileResponse = await getCurrentUser();

        setUserRole(profileResponse.data.roleId);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setIsSuperAdmin(parseInt(userRole) === role.administrator);
  }, [userRole]);

  useEffect(() => {
    fetchCitiesData();
  }, [fetchCitiesData]);

  // Reset search query when switching tabs
  useEffect(() => {
    setSearchQuery("");
  }, [activeTab]);

  // Debounce search query
  useEffect(() => {
    if (
      activeTab === "userList" ||
      activeTab === "cityAdminList" ||
      activeTab === "superCityAdminList"
    ) {
      const timer = setTimeout(() => {
        if (activeTab === "userList") {
          getUsersLists(1, 10);
        } else if (activeTab === "cityAdminList") {
          getCityAdminLists(1, 10);
        } else if (activeTab === "superCityAdminList") {
          getSuperCityAdminLists(1, 10);
        }
      }, 500); // 500ms debounce

      return () => clearTimeout(timer);
    }
  }, [
    searchQuery,
    activeTab,
    getUsersLists,
    getCityAdminLists,
    getSuperCityAdminLists,
  ]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 3000);

      return () => clearTimeout(timer); // Cleanup if component unmounts
    }
  }, [success]);
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 3000);

      return () => clearTimeout(timer); // Cleanup if component unmounts
    }
  }, [error]);

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: "userList", label: t("users") || "Users" },
      { id: "cityAdminList", label: t("cityAdmins") || "City Admins" },
    ];
    if (isSuperAdmin) {
      baseTabs.push({
        id: "superCityAdminList",
        label: t("superCityAdmins") || "Super City Admins",
      });
    }
    baseTabs.push({ id: "adminList", label: t("Admin list") || "Admin List" });

    return baseTabs;
  }, [isSuperAdmin, t]);

  const handleActiveTab = (tabId) => {
    setActiveTab(tabId);
    setSearchQuery("");
    setSelectedCityFilter(null);
  };
  return (
    <div className="bg-gray-800 body-font relative min-h-screen">
      <SideBar />
      {success && (
        <div className="fixed top-4 right-4 z-[9999] p-3 bg-green-100 text-green-800 rounded-md text-xs shadow-lg">
          {success}
        </div>
      )}

      {/* ✅ ERROR MESSAGE */}
      {error && (
        <div className="fixed top-4 right-4 z-[9999] p-3 bg-red-100 text-red-800 rounded-md text-xs shadow-lg">
          {error}
        </div>
      )}
      <div className="container w-auto px-5 py-2">
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-white">
              {t("user_management") || "Users Management"}
            </h1>
          </div>

          {/* Tabs with City Filter */}
          <div className="mb-6 border-b border-gray-600">
            <div className="flex justify-between items-center">
              {/* Tabs */}
              <div className="flex space-x-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleActiveTab(tab.id)}
                    className={`pb-3 px-4 text-sm font-medium transition-colors relative ${
                      activeTab === tab.id
                        ? "text-blue-500 border-b-2 border-blue-500"
                        : "text-gray-400 hover:text-gray-200"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* City Filter and Search - Aligned Right */}
              <div className="pb-3 flex gap-3 items-center">
                {activeTab === "adminList" && (
                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition"
                    onClick={() => setShowInviteModal(true)}
                  >
                    {t("invite_user")}
                  </button>
                )}
                {(activeTab === "userList" ||
                  activeTab === "cityAdminList" ||
                  activeTab === "superCityAdminList") && (
                  <div className="relative min-w-[250px]">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t("search") || "Search users..."}
                      className="w-full px-4 py-2 text-sm text-gray-200 bg-gray-700 border border-gray-600 rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:border-gray-500 placeholder-gray-400"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
                {(activeTab === "cityAdminList" ||
                  activeTab === "superCityAdminList") &&
                  isSuperAdmin && (
                    <div className="relative min-w-[250px]">
                      <Select
                        primaryColor="indigo"
                        value={selectedCityFilter}
                        onChange={setSelectedCityFilter}
                        options={citiesList}
                        placeholder={t("filter_by_City") || "Filter by City"}
                        isClearable
                        classNames={{
                          menuButton: ({ isDisabled }) =>
                            `flex text-sm text-gray-200 border border-gray-600 rounded-lg shadow-sm transition-all duration-200 focus:outline-none ${
                              isDisabled
                                ? "bg-gray-700"
                                : "bg-gray-700 hover:border-gray-500"
                            }`,
                          menu: "absolute z-50 w-full bg-white shadow-lg border border-gray-200 rounded-lg py-1 mt-1.5 text-sm text-gray-700",
                          listItem: ({ isSelected }) =>
                            `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
                              isSelected
                                ? `text-white bg-indigo-600`
                                : `text-gray-700 hover:bg-indigo-100 hover:text-indigo-900`
                            }`,
                        }}
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === "userList" && (
              <UnifiedTable
                data={usersList}
                handleAction={handleAction}
                onManagePermission={handleManagePermission}
                isSuperAdmin={isSuperAdmin}
                getDataLists={getUsersLists}
                tableType="user"
                emptyMessageKey="no_users_found"
              />
            )}
            {activeTab === "cityAdminList" && (
              <UnifiedTable
                data={cityAdminList}
                handleAction={handleAction}
                onManageRole={handleManageRole}
                isSuperAdmin={isSuperAdmin}
                getDataLists={getCityAdminLists}
                tableType="cityAdmin"
                emptyMessageKey="no_city_admins_found"
              />
            )}
            {activeTab === "superCityAdminList" && (
              <UnifiedTable
                data={superCityAdminList}
                handleAction={handleAction}
                onManageRole={handleManageSuperCityAdminRole}
                isSuperAdmin={isSuperAdmin}
                getDataLists={getSuperCityAdminLists}
                tableType="superCityAdmin"
                emptyMessageKey="no_super_city_admins_found"
              />
            )}
            {activeTab === "adminList" && (
              <AdminListTable
                adminList={adminList}
                isSuperAdmin={isSuperAdmin}
              />
            )}
          </div>

          {/* Create Admin Modal */}
          {showInviteModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-2xl p-6 max-w-md w-full relative">
                <button
                  className="absolute top-2 right-3 text-gray-500 hover:text-black text-2xl"
                  // onClick={() => setShowInviteModal(false)}
                  onMouseDown={(e) => {
                    e.preventDefault(); // ✅ KEY
                    e.stopPropagation(); // ✅ KEY
                    setShowInviteModal(false);
                  }}
                >
                  &times;
                </button>
                <CreateAdmin
                  onSuccess={handleAddAdmin}
                  isSuperAdmin={isSuperAdmin}
                />
              </div>
            </div>
          )}

          {/* User Permission Modal */}
          {showPermissionModal && selectedUser && (
            <UserPermissionModal
              user={selectedUser}
              onClose={() => {
                setShowPermissionModal(false);
                setSelectedUser(null);
              }}
              onSave={handleSavePermission}
              isSuperAdmin={isSuperAdmin}
            />
          )}

          {/* Admin Role Modal */}
          {showRoleModal && selectedAdmin && (
            <AdminRoleModal
              admin={selectedAdmin}
              onClose={() => {
                setShowRoleModal(false);
                setSelectedAdmin(null);
              }}
              onSave={handleSaveRole}
              isSuperAdmin={isSuperAdmin}
            />
          )}

          {/* Super City Admin Role Modal */}
          {showSuperCityAdminRoleModal && selectedSuperCityAdmin && (
            <SuperCityAdminRoleModal
              admin={selectedSuperCityAdmin}
              onClose={() => {
                setShowSuperCityAdminRoleModal(false);
                setSelectedSuperCityAdmin(null);
              }}
              onSave={handleSaveSuperCityAdminRole}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersManagement;
